import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useSocket } from '../../context/SocketContext';
import { useTheme } from '../../context/ThemeContext';
import toast from 'react-hot-toast';
import './Header.css';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const Header = ({ userRole, sidebarCollapsed }) => {
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);
  const [isNotificationDropdownOpen, setIsNotificationDropdownOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [unreadMessagesCount, setUnreadMessagesCount] = useState(0);
  const [loadingNotifications, setLoadingNotifications] = useState(false);
  const [avatarLoadFailed, setAvatarLoadFailed] = useState(false);
  const [isOnline, setIsOnline] = useState(true);

  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [showSearchDropdown, setShowSearchDropdown] = useState(false);
  const searchRef = useRef(null);
  const abortControllerRef = useRef(null);
  const debounceTimerRef = useRef(null);

  const profileDropdownRef = useRef(null);
  const notificationDropdownRef = useRef(null);

  const { logout, user, token } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const socket = useSocket();
  const navigate = useNavigate();
  const location = useLocation();

  const fetchNotifications = useCallback(async () => {
    if (!token) return;
    setLoadingNotifications(true);
    try {
      const response = await fetch(`${API_BASE}/notifications`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      if (data.success) {
        setNotifications(data.notifications || []);
        setUnreadCount(data.unreadCount || 0);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingNotifications(false);
    }
  }, [token]);

  const fetchUnreadMessages = useCallback(async () => {
    if (!token) return;
    try {
      const response = await fetch(`${API_BASE}/messages/unread-count`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      if (data.success) {
        setUnreadMessagesCount(data.count || 0);
      }
    } catch (error) {
      console.error(error);
    }
  }, [token]);

  useEffect(() => {
    fetchNotifications();
    fetchUnreadMessages();
    const poll = setInterval(() => {
      fetchNotifications();
      fetchUnreadMessages();
    }, 30000);
    return () => clearInterval(poll);
  }, [fetchNotifications, fetchUnreadMessages]);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    setIsOnline(navigator.onLine);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  useEffect(() => {
    if (!socket) return;
    const refreshAll = () => {
      fetchUnreadMessages();
      fetchNotifications();
    };
    socket.on('new_message', refreshAll);
    socket.on('messages_read', () => fetchUnreadMessages());
    socket.on('new_bid', refreshAll);
    socket.on('bid_accepted', refreshAll);
    socket.on('contract_status_change', refreshAll);
    socket.on('work_completed', refreshAll);
    socket.on('payment_released', refreshAll);
    return () => {
      socket.off('new_message', refreshAll);
      socket.off('messages_read', () => fetchUnreadMessages());
      socket.off('new_bid', refreshAll);
      socket.off('bid_accepted', refreshAll);
      socket.off('contract_status_change', refreshAll);
      socket.off('work_completed', refreshAll);
      socket.off('payment_released', refreshAll);
    };
  }, [socket, fetchNotifications, fetchUnreadMessages]);

  const performSearch = useCallback(async () => {
    if (!token) return;
    if (abortControllerRef.current) abortControllerRef.current.abort();
    const query = searchQuery.trim();
    if (query.length < 2) {
      setSearchResults([]);
      setShowSearchDropdown(false);
      return;
    }
    setSearchLoading(true);
    const controller = new AbortController();
    abortControllerRef.current = controller;
    try {
      const response = await fetch(
        `${API_BASE}/search?q=${encodeURIComponent(query)}&role=${user?.role}`,
        { headers: { Authorization: `Bearer ${token}` }, signal: controller.signal }
      );
      const data = await response.json();
      if (data.success) {
        setSearchResults(data.results.slice(0, 8));
        setShowSearchDropdown(data.results.length > 0);
      } else {
        setSearchResults([]);
      }
    } catch (err) {
      if (err.name !== 'AbortError') console.error('Search error:', err);
    } finally {
      setSearchLoading(false);
      abortControllerRef.current = null;
    }
  }, [searchQuery, token, user?.role]);

  useEffect(() => {
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = setTimeout(() => performSearch(), 400);
    return () => clearTimeout(debounceTimerRef.current);
  }, [searchQuery, performSearch]);

  const handleResultClick = (result) => {
    if (!result._id) return;
    setShowSearchDropdown(false);
    setSearchQuery('');
    setTimeout(() => {
      try {
        if (result.type === 'vessel') {
          if (user?.role === 'owner') navigate(`/owner/ship/${result._id}`);
        } else if (result.type === 'rfq') {
          if (user?.role === 'owner') navigate(`/owner/job/${result._id}`);
          else if (user?.role === 'service-provider') navigate(`/service-provider/job/${result._id}`);
        } else if (result.type === 'contract') {
          if (user?.role === 'service-provider') navigate(`/service-provider/contract/${result._id}`);
          else if (user?.role === 'owner') navigate(`/owner/contract/${result._id}`);
        }
      } catch (err) {
        console.error(err);
        toast.error('Navigation failed');
      }
    }, 100);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target))
        setShowSearchDropdown(false);
      if (profileDropdownRef.current && !profileDropdownRef.current.contains(event.target))
        setIsProfileDropdownOpen(false);
      if (notificationDropdownRef.current && !notificationDropdownRef.current.contains(event.target))
        setIsNotificationDropdownOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const markAsRead = async (notificationId) => {
    try {
      await fetch(`${API_BASE}/notifications/${notificationId}/read`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchNotifications();
    } catch (error) {
      console.error(error);
    }
  };

  const markAllAsRead = async () => {
    try {
      await fetch(`${API_BASE}/notifications/read-all`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchNotifications();
    } catch (error) {
      console.error(error);
    }
  };

  const handleNotificationClick = (notification) => {
    if (!notification.isRead) markAsRead(notification._id);
    if (notification.link) navigate(notification.link);
    setIsNotificationDropdownOpen(false);
  };

  const getNotificationIcon = (type) => {
    const icons = {
      bid: 'fa-gavel',
      payment: 'fa-wallet',
      contract: 'fa-file-contract',
      report: 'fa-file-alt',
      dispute: 'fa-scale-balanced',
      job: 'fa-briefcase',
      message: 'fa-envelope',
      system: 'fa-bell'
    };
    return icons[type] || 'fa-bell';
  };

  const getTimeAgo = (date) => {
    const seconds = Math.floor((new Date() - new Date(date)) / 1000);
    if (seconds < 60) return 'Just now';
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    if (days < 7) return `${days}d ago`;
    return new Date(date).toLocaleDateString();
  };

  const getPageName = () => {
    const path = location.pathname;
    const lastSegment = path.split('/').pop();
    const pageNames = {
      dashboard: 'Dashboard',
      'my-ships': 'My Fleet',
      'post-job': 'Post Job',
      'my-jobs-bids': 'Jobs & Offers',
      'my-jobs-quotations': 'Quotations',
      payments: 'Payments',
      certificates: 'Documents',
      tasks: 'Tasks',
      analytics: 'Analytics',
      'available-jobs': 'Available Jobs',
      'my-offers': 'My Quotes',
      'active-work': 'Active Work',
      'work-reports': 'Work Reports',
      'company-profile': 'Profile',
      'verify-service-providers': 'Verifications',
      disputes: 'Disputes',
      'payment-releases': 'Releases',
      users: 'Users',
      reports: 'Reports',
      settings: 'Settings',
      profile: 'My Profile',
      messages: 'Messages',
      notifications: 'Notifications',
      credits: 'Credits',
      buy: 'Buy Credits',
      transactions: 'Transactions',
      'credit-wallets': 'Credit Wallets',
      'credit-purchases': 'Credit Purchases'
    };
    return pageNames[lastSegment] || 'Dashboard';
  };

  const getInitials = () => {
    if (!user?.name) return 'U';
    const parts = user.name.split(' ');
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    return user.name.charAt(0).toUpperCase();
  };

  const userId = user?.id || user?._id;
  const avatarUrl = userId ? `${API_BASE}/avatar/${userId}` : null;
  const showImage = avatarUrl && !avatarLoadFailed;
  const handleImageError = () => setAvatarLoadFailed(true);

  return (
    <header className={`main-header ${sidebarCollapsed ? 'collapsed' : ''}`}>
      <div className="header-left">
        <span className="page-name">{getPageName()}</span>
      </div>

      <div className="header-center">
        <div className="search-wrapper" ref={searchRef}>
          <i className="fa-solid fa-search search-icon"></i>
          <input
            type="text"
            placeholder="Search vessels, jobs, contracts..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onFocus={() => searchQuery.trim().length >= 2 && setShowSearchDropdown(true)}
          />
          {showSearchDropdown && (
            <div className="search-dropdown">
              {searchLoading && <div className="search-loading">Searching...</div>}
              {!searchLoading && searchResults.length === 0 && searchQuery.trim().length >= 2 && (
                <div className="search-no-results">No results found</div>
              )}
              {searchResults.map((result) => (
                <div
                  key={result._id}
                  className="search-result-item"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleResultClick(result);
                  }}
                >
                  <div className="search-result-icon">
                    <i className={`fas ${result.type === 'vessel' ? 'fa-ship' : result.type === 'rfq' ? 'fa-briefcase' : 'fa-file-contract'}`}></i>
                  </div>
                  <div className="search-result-info">
                    <div className="search-result-title">
                      {result.type === 'vessel' ? result.name : result.vesselName}
                    </div>
                    <div className="search-result-subtitle">
                      {result.type === 'vessel' ? `IMO: ${result.imo}` : result.serviceType}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="header-right">
        <button
          className="theme-toggle-btn"
          onClick={toggleTheme}
          title={theme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}
          aria-label="Toggle theme"
        >
          <i className={`fa-solid ${theme === 'dark' ? 'fa-sun' : 'fa-moon'}`}></i>
        </button>

        <div className="icon-btn-wrapper">
          <button className="icon-btn" onClick={() => navigate('/messages')}>
            <i className="fa-regular fa-comment-dots"></i>
            {unreadMessagesCount > 0 && (
              <span className="message-badge">
                {unreadMessagesCount > 9 ? '9+' : unreadMessagesCount}
              </span>
            )}
          </button>
        </div>

        <div
          className={`notification-wrapper ${isNotificationDropdownOpen ? 'active' : ''}`}
          ref={notificationDropdownRef}
        >
          <button
            className="icon-btn"
            onClick={() => setIsNotificationDropdownOpen(!isNotificationDropdownOpen)}
          >
            <i className="fa-regular fa-bell"></i>
            {unreadCount > 0 && (
              <span className="notification-badge">
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
          </button>

          {isNotificationDropdownOpen && (
            <div className="notification-dropdown">
              <div className="dropdown-header">
                <span className="dropdown-title">Notifications</span>
                {unreadCount > 0 && (
                  <button className="mark-all-btn" onClick={markAllAsRead}>
                    Mark all read
                  </button>
                )}
              </div>
              <div className="notification-list">
                {loadingNotifications ? (
                  <div style={{ padding: '30px', textAlign: 'center', color: '#666', fontSize: '12px' }}>
                    Loading...
                  </div>
                ) : notifications.length === 0 ? (
                  <div style={{ padding: '30px', textAlign: 'center', color: '#666', fontSize: '12px' }}>
                    No notifications
                  </div>
                ) : (
                  notifications.slice(0, 5).map((notif) => (
                    <div
                      key={notif._id}
                      className={`notification-item ${!notif.isRead ? 'unread' : ''}`}
                      onClick={() => handleNotificationClick(notif)}
                    >
                      <div className="notification-icon">
                        <i className={`fa-solid ${getNotificationIcon(notif.type)}`}></i>
                      </div>
                      <div className="notification-content">
                        <div className="notification-title">{notif.title}</div>
                        <div className="notification-message">{notif.message}</div>
                        <div className="notification-time">{getTimeAgo(notif.createdAt)}</div>
                      </div>
                      {!notif.isRead && <div className="unread-dot"></div>}
                    </div>
                  ))
                )}
              </div>
              <div className="dropdown-footer">
                <button onClick={() => navigate('/notifications')}>View all</button>
              </div>
            </div>
          )}
        </div>

        <div
          className={`avatar-wrapper ${isProfileDropdownOpen ? 'active' : ''}`}
          ref={profileDropdownRef}
        >
          <button
            className="avatar-btn"
            onClick={() => setIsProfileDropdownOpen(!isProfileDropdownOpen)}
          >
            <div className="user-avatar">
              {showImage ? (
                <img src={avatarUrl} alt="Avatar" onError={handleImageError} />
              ) : (
                getInitials()
              )}
              <span
                className={`user-avatar-status ${isOnline ? 'online' : 'offline'}`}
                aria-label={isOnline ? 'Online' : 'Offline'}
              ></span>
            </div>
          </button>

          {isProfileDropdownOpen && (
            <div className="profile-dropdown">
              <div className="dropdown-header">
                <span className="dropdown-name">{user?.name || 'User'}</span>
                <span className="dropdown-email">{user?.email || ''}</span>
              </div>
              <div className="dropdown-menu">
                <button
                  className="dropdown-item"
                  onClick={() => navigate(`/${userRole}/profile`)}
                >
                  <i className="fa-regular fa-user"></i> My Profile
                </button>
                <button
                  className="dropdown-item"
                  onClick={() => navigate(`/${userRole}/settings`)}
                >
                  <i className="fa-solid fa-gear"></i> Settings
                </button>
                <button
                  className="dropdown-item"
                  onClick={() => navigate('/messages')}
                >
                  <i className="fa-regular fa-comment-dots"></i> Messages
                </button>
                <button
                  className="dropdown-item"
                  onClick={() => navigate('/notifications')}
                >
                  <i className="fa-regular fa-bell"></i> Notifications
                </button>
                <div className="dropdown-divider"></div>
                <button className="dropdown-item logout" onClick={handleLogout}>
                  <i className="fa-solid fa-arrow-right-from-bracket"></i> Logout
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;